/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { task } from 'ember-concurrency';

import IdentityModel from 'cio/models/identity';
import ApiErrorsService from 'cio/services/api-errors';
import { FormValues } from 'cio/components/from-identity-modal';
import AlertsService from 'cio/services/alerts';

interface FromIdentityRowArgs {
  canVerify: boolean;
  domainName?: string;
  identity: IdentityModel;
  identities: IdentityModel[];
  identitiesInUse?: IdentityModel[];
  readOnly: boolean;
  showVerificationStatus: boolean;
  onUpdateIdentity: () => unknown;
  showIdCell?: boolean;
}

export default class FromIdentityRow extends Component<FromIdentityRowArgs> {
  @service alerts!: AlertsService;
  @service apiErrors!: ApiErrorsService;

  @tracked isEditing = false;
  @tracked isDeleting = false;

  get showIdCell() {
    return this.args.showIdCell ?? true;
  }

  get fromIdentityDropdownOptions() {
    return [
      {
        label: 'View usage',
        link: {
          route: 'message-library',
          query: { query: this.args.identity.email },
        },
      },
      {
        label: 'Edit',
        action: this.toggleIsEditing,
      },
      {
        label: 'Delete',
        classNames: 'is-danger text-error',
        typeName: ' ',
        disabled: this.inUse || this.args.readOnly,
        action: this.toggleIsDeleting,
        tooltip: {
          condition: this.inUse,
          content: 'You cannot delete a from address that is in use',
        },
      },
    ];
  }

  get inUse() {
    return this.args.identitiesInUse?.includes(this.args.identity) ?? false;
  }

  @action
  toggleIsDeleting() {
    this.isDeleting = !this.isDeleting;
  }

  @action
  toggleIsEditing() {
    this.isEditing = !this.isEditing;
  }

  saveIdentityTask = task(async ({ name, email }: FormValues) => {
    this.args.identity.setProperties({ name, email });

    try {
      await this.args.identity.save();

      if (this.args.onUpdateIdentity) {
        await this.args.onUpdateIdentity();
      }

      this.alerts.success('identity.save.success', 'From address saved');
      this.isEditing = false;
    } catch (reason) {
      this.apiErrors.report(reason);
    }
  });

  @action
  discardChanges() {
    this.args.identity.rollbackAttributes();
    this.isEditing = false;
  }

  deleteIdentityTask = task({ drop: true }, async () => {
    try {
      await this.args.identity.destroyRecord();
      this.isDeleting = false;
    } catch (e) {
      this.apiErrors.report(e);
    }
  });
}
